import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Radio, message, Space, Dropdown, Menu, Checkbox, Popover, Tooltip } from 'antd';
import ListView from '../../components/VacancyRequests/ListView';
import KanbanView from '../../components/VacancyRequests/KanbanView';
import CreateModal from '../../components/VacancyRequests/CreateModal';
import CreateFromTemplateModal from '../../components/VacancyRequests/CreateFromTemplateModal';
import EditModal from '../../components/VacancyRequests/EditModal';
import ViewModal from '../../components/VacancyRequests/ViewModal';
import { Outlet } from 'react-router-dom';
import CopyModal from '../../components/VacancyRequests/CopyModal';
import styles from '../../components/VacancyRequests/VacancyRequests.module.css';
import { DownOutlined, SettingOutlined } from '@ant-design/icons';
import { fetchVacancyRequests, createVacancyRequest, updateVacancyRequest, deleteVacancyRequest, updateVacancyRequestStatus, updateRequestStatusOptimistic, revertRequestStatus, setShowAllRequests } from "../../redux/vacancyRequestsSlice";
import { fetchUsers } from "../../redux/userOrganisationsSlice";
import { fetchVacancies } from "../../redux/vacanciesSlice";
import { columnLabels, STATUSES } from '../../constants/vacancyRequestColumnLabels';
import { canCreateVacancyRequest, canChangeVacancyRequestStatus } from '../../utils/vacancyRequestPermissions';

const allColumnKeys = Object.keys(columnLabels);

const defaultColumns = [
  'id',
  'job_title',
  'positions_count',
  'author',
  'organisation',
  'department',
  'assignee_id',
  'planned_start_date',
  'status',
  'actions'
];

const VacancyRequestsPage = () => {
  const { user, orgId, role } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { list: requests, loading: requestsLoading, showAllRequests, totalPages, totalItems } = useSelector(state => state.vacancyRequests);
  const { organisations, loading: orgsLoading } = useSelector(state => state.organisationDepartment);
  const { list: users, loading: usersLoading } = useSelector(state => state.userOrganisations);
  const { list: vacancies, loading: vacanciesLoading } = useSelector(state => state.vacancies);
  const [viewMode, setViewMode] = useState('list');
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);
  const [selectedStatuses, setSelectedStatuses] = useState(['new', 'pending', 'in_progress', 'closed', 'withdrawn', 'rejected', 'archived']);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createFromTemplateModalVisible, setCreateFromTemplateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [columnsDropdownOpen, setColumnsDropdownOpen] = useState(false);
  const [statusesDropdownOpen, setStatusesDropdownOpen] = useState(false);

  // Состояние пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Состояние фильтров
  const [filters, setFilters] = useState({});

  useEffect(() => {
    const orgIdToFetch = showAllRequests ? "all" : orgId;
    const skip = (currentPage - 1) * pageSize;

    // Преобразуем фильтры для бэкенда в нормальном формате
    const backendFilters = {};
    // Маппинг ключей фронтенда на ключи бэкенда
    // const keyMapping = {
    //   'organisation': 'organisation',
    //   'department': 'department'
    // };

    Object.keys(filters).forEach(key => {
      const value = filters[key];

      if (value === null || value === undefined || value === '') {
        return;
      }

      const backendKey = key;

      if (Array.isArray(value)) {
        const filteredArray = value.filter(v => v !== null && v !== undefined && v !== '');
        if (filteredArray.length > 0) {
          backendFilters[backendKey] = filteredArray.join(',');
        }
      } else if (typeof value === 'string' || typeof value === 'number') {
        backendFilters[backendKey] = value;
      } else if (typeof value === 'boolean') {
        backendFilters[backendKey] = value;
      }
    });

    dispatch(fetchVacancyRequests({ skip, limit: pageSize, org_id: orgIdToFetch, filters: backendFilters }));
  }, [dispatch, orgId, showAllRequests, currentPage, pageSize, filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [showAllRequests, filters]);

  useEffect(() => {
    dispatch(fetchUsers({ skip: 0, limit: 20 }));
    dispatch(fetchVacancies({ skip: 0, limit: 20, org_id: "all" }));
  }, [dispatch, orgId]);

  const handleCreate = (formData) => {
    dispatch(createVacancyRequest({ organisationId: orgId, vacancyRequest: formData }))
      .unwrap()
      .then(() => {
        message.success('Заявка успешно создана');
        setCreateModalVisible(false);
      })
      .catch(() => message.error('Ошибка при создании заявки'));
    if (showAllRequests) {
      dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: "all" })).unwrap();
    } else {
      dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: orgId })).unwrap();
    }
  };

  const handleUpdate = async (id, organisationId, formData) => {
    try {
      if (organisationId !== orgId) {
        await dispatch(updateVacancyRequest({ id: id, organisationId: organisationId, vacancyRequest: formData, showAllRequests: showAllRequests })).unwrap();
      } else {
        await dispatch(updateVacancyRequest({ id: id, organisationId: orgId, vacancyRequest: formData, showAllRequests: showAllRequests })).unwrap();
      }

      setEditModalVisible(false);
      setCreateFromTemplateModalVisible(false);

      const orgIdToFetch = showAllRequests ? "all" : orgId;
      const skip = (currentPage - 1) * pageSize;
      await dispatch(fetchVacancyRequests({ skip, limit: pageSize, org_id: orgIdToFetch, filters })).unwrap();
    } catch (error) {
      message.error('Ошибка при обновлении заявки');
    }
  };

  const handleDelete = (id) => {

    dispatch(deleteVacancyRequest({ id: id, organisationId: orgId, showAllRequests: showAllRequests }))
      .unwrap()
      .catch(() => message.error('Ошибка при удалении заявки'));
  };

  const requestsTemplate = requests.filter(r => r.is_template);
  // const activeRequests = requests.filter(r => !r.is_template);
  const activeRequests = requests

  const loading = requestsLoading || orgsLoading || usersLoading || vacanciesLoading;

  const requiresCommentForEdit = (status) => {
    return ['pending', 'new', 'in_progress'].includes(status);
  };

  const handleEditRequest = (request) => {
    if (requiresCommentForEdit(request.status)) {
      message.info('При редактировании заявки в статусе "' +
        (request.status === 'pending' ? 'На согласовании' :
          request.status === 'new' ? 'Новая' : 'В работе') +
        '" обязательно укажите причину изменения в комментарии');
    }
    setSelectedRequest(request);
    setEditModalVisible(true);
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setViewModalVisible(true);
  };

  const handleCopyRequest = (request) => {
    setSelectedRequest(request);
    setCopyModalVisible(true);
  };

  const handleStatusChange = async (item, newStatus, newIndex) => {
    // Проверяем права на изменение статуса
    if (!canChangeVacancyRequestStatus(role)) {
      message.error('У вас недостаточно прав для изменения статуса заявки');
      return;
    }

    const originalStatus = item.status;

    try {
      dispatch(updateRequestStatusOptimistic({ id: item.id, status: newStatus }));

      await dispatch(updateVacancyRequestStatus({
        id: item.id,
        organisationId: orgId,
        status: newStatus
      })).unwrap();
      // Обновляем текущую страницу
      const orgIdToFetch = showAllRequests ? "all" : orgId;
      const skip = (currentPage - 1) * pageSize;
      await dispatch(fetchVacancyRequests({ skip, limit: pageSize, org_id: orgIdToFetch, filters })).unwrap();
      message.success('Статус заявки обновлён');
    } catch (error) {
      dispatch(revertRequestStatus({ id: item.id, originalStatus }));
      message.error('Ошибка при обновлении статуса');
    }
  };

  const handleCommentSubmit = async (id, comment) => {
    try {
      dispatch(updateVacancyRequest({ id: id, organisationId: orgId, vacancyRequest: { comment: comment } }))
        .unwrap()
        .then(() => {
          setEditModalVisible(false);
          setCreateFromTemplateModalVisible(false);
        })
        .catch(() => message.error('Ошибка при оставлении комментария для заявки'));
      // if (showAllRequests) {
      //   dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: "all" })).unwrap();
      // } else {
      //   dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: orgId })).unwrap();
      // }
    } catch (error) {
      message.error('Ошибка при обновлении заявки');
    }
  };


  return (
    <div className={styles.container} >
      <div className={styles.controls}>
        <h1 style={{ margin: '0px', marginRight: 'auto', paddingRight: '20px' }}>Заявки на подбор персонала</h1>
        <Space wrap>
          {(role === 'superadmin' || role === 'admin') && (
            <Tooltip title={showAllRequests ? "Вернуться к просмотру заявок только вашей организации" : "Показать заявки из всех организаций"}>
              <Button
                onClick={() => {
                  // Изменяем только состояние, запрос сделает useEffect
                  dispatch(setShowAllRequests(!showAllRequests));
                }}
                type={showAllRequests ? "primary" : "default"}
              >
                {showAllRequests ? "Показать заявки для выбранной организации" : "Получить заявки по всем организациям"}
              </Button>
            </Tooltip>
          )}
          {viewMode === 'list' ? (
            <>
              <Tooltip title="Настроить отображение колонок в таблице">
                <Popover
                  open={columnsDropdownOpen}
                  onOpenChange={setColumnsDropdownOpen}
                  content={
                    <div style={{ maxHeight: '70vh', overflowY: 'auto', minWidth: '200px' }}>
                      {allColumnKeys.map(key => (
                        <div key={key} style={{ padding: '4px 0' }}>
                          <Checkbox
                            checked={selectedColumns.includes(key)}
                            onChange={(e) => {
                              setSelectedColumns(prev => {
                                return prev.includes(key)
                                  ? prev.filter(col => col !== key)
                                  : [...prev, key];
                              });
                            }}
                          >
                            {columnLabels[key]}
                          </Checkbox>
                        </div>
                      ))}
                    </div>
                  }
                  trigger="click"
                  placement="bottomLeft"
                >
                  <Button icon={<SettingOutlined />}>
                    Настройка колонок
                  </Button>
                </Popover>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Настроить отображение статусов в канбане">
              <Popover
                open={statusesDropdownOpen}
                onOpenChange={setStatusesDropdownOpen}
                content={
                  <div style={{ maxHeight: '70vh', overflowY: 'auto', minWidth: '200px' }}>
                    {Object.keys(STATUSES)
                      .filter(key => key !== 'template') // Исключаем статус "template" из настроек канбана
                      .map(key => (
                        <div key={key} style={{ padding: '4px 0' }}>
                          <Checkbox
                            checked={selectedStatuses.includes(key)}
                            onChange={(e) => {
                              setSelectedStatuses(prev => {
                                return prev.includes(key)
                                  ? prev.filter(status => status !== key)
                                  : [...prev, key];
                              });
                            }}
                          >
                            {STATUSES[key].title}
                          </Checkbox>
                        </div>
                      ))}
                  </div>
                }
                trigger="click"
                placement="bottomLeft"
              >
                <Button icon={<SettingOutlined />}>
                  Настройка канбана
                </Button>
              </Popover>
            </Tooltip>
          )}

          <Radio.Group
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            buttonStyle="solid"
          >
            <Tooltip title="Просмотр заявок в виде таблицы">
              <Radio.Button value="list">Список</Radio.Button>
            </Tooltip>
            <Tooltip title="Просмотр заявок в виде канбан-доски">
              <Radio.Button value="kanban">Канбан</Radio.Button>
            </Tooltip>
          </Radio.Group>
          {canCreateVacancyRequest(role) && (
            <Tooltip title="Создать новую заявку на подбор персонала">
              <Dropdown
                overlay={
                  <Menu
                    onClick={({ key }) => {
                      switch (key) {
                        case 'new':
                          setCreateModalVisible(true);
                          break;
                        case 'from_request':
                          setCreateFromTemplateModalVisible(true)
                          break;
                        case 'from_draft':
                          setCreateFromTemplateModalVisible(true)
                          break;
                        default:
                          break;
                      }
                    }}
                  >
                    <Menu.Item key="new">Новая</Menu.Item>
                    {/* <Menu.Item key="from_request">Копировать из заявки</Menu.Item> */}
                    <Menu.Item key="from_draft">Создать из черновика</Menu.Item>
                  </Menu>
                }
              >
                <Button type="primary">
                  Создать <DownOutlined />
                </Button>
              </Dropdown>
            </Tooltip>
          )}
        </Space>
      </div>

      {
        viewMode === 'list' ? (
          <ListView
            requests={activeRequests}
            loading={loading}
            onDelete={handleDelete}
            onEditRequest={handleEditRequest}
            onViewRequest={handleViewRequest}
            onCopyRequest={handleCopyRequest}
            userRole={role}
            visibleColumns={selectedColumns}
            organisations={organisations}
            users={users}
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={(page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
                setCurrentPage(1);
              }
            }}
            filters={filters}
            onFiltersChange={setFilters}
          />
        ) : (
          <KanbanView
            requests={activeRequests}
            loading={loading}
            onStatusChange={handleStatusChange}
            onViewRequest={handleViewRequest}
            onDelete={handleDelete}
            onEditRequest={handleEditRequest}
            vacancies={vacancies}
            organisations={organisations}
            STATUSES={STATUSES}
            selectedStatuses={selectedStatuses}
            users={users}
          />
        )
      }

      <CreateModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onSubmit={handleCreate}
      />

      <CreateFromTemplateModal
        visible={createFromTemplateModalVisible}
        onCancel={() => setCreateFromTemplateModalVisible(false)}
        onSubmit={handleUpdate}
        onCommentSubmit={handleCommentSubmit}
        requestsTemplate={requestsTemplate}
        organisations={organisations}
        users={users}
      />

      <EditModal
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onSubmit={handleUpdate}
        request={selectedRequest}
        organisations={organisations}
        users={users}
      />

      <ViewModal
        visible={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        onCommentSubmit={handleCommentSubmit}
        request={selectedRequest}
        vacancies={vacancies}
        organisations={organisations}
        users={users}
        currentUserId={user?.id}
      />

      <Outlet />

      <CopyModal
        visible={copyModalVisible}
        onCancel={() => setCopyModalVisible(false)}
        originalRequest={selectedRequest}
        organisations={organisations}
        users={users}
      />
    </div >
  );
};

export default VacancyRequestsPage;